// src/components/pages/MuultiSpaceAdminDashboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import {
    collection,
    limit,
    orderBy,
    query,
    startAfter,
    getDocs,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import useAdminSession from '../../hooks/useAdminSession';
import { openPublishSessionForWallet } from "../../utils/auth";
import { timestampMs } from "../../utils/activityEvents";
import { loadAdminActivityEvents } from "../../utils/adminActivityClient";
import "../MuultiStyle.css";
import PublishingFeePolicy from "../PublishingFeePolicy";
import PublishingCredentials from "../PublishingCredentials";
import NotificationInbox from "../NotificationInbox";
import UsageAnalytics from "../UsageAnalytics";
import usageContract from '../../../functions/usageContract.js';

const SPACES_PAGE_SIZE = 25;
const ANALYTICS_PAGE_SIZE = 50;
const ADMIN_DISPLAY_PAGE_SIZE = 25;
// ✅ Your activities in Firestore clearly have publishedAt, and sometimes do NOT have createdAt.
// So we order/paginate using publishedAt to avoid “No activity yet” when docs exist.
const ORDER_FIELD = "publishedAt";

function fmtTime(ts) {
    try {
        const ms = timestampMs(ts);
        const d = ms ? new Date(ms) : null;
        return d ? d.toLocaleString() : "";
    } catch {
        return "";
    }
}

function safeStr(v) {
    return v == null ? "" : String(v);
}

function prettyJson(v) {
    try {
        return JSON.stringify(v, null, 2);
    } catch {
        return safeStr(v);
    }
}

function clampText(v, max = 220) {
    const t = safeStr(v).trim();
    if (!t) return "";
    return t.length > max ? t.slice(0, max) + "…" : t;
}

function pickActivityText(a) {
    // Prefer real content stored in meta
    const metaContent =
        a?.meta?.content ??
        a?.meta?.text ??
        a?.meta?.componentSummary?.excerpt ??
        a?.meta?.component?.excerpt ??
        a?.meta?.componentSummary?.title ??
        a?.meta?.componentSummary?.heading ??
        a?.meta?.componentSummary?.caption ??
        a?.meta?.component?.title ??
        a?.meta?.component?.heading ??
        a?.meta?.component?.caption ??
        a?.meta?.object?.content ??
        a?.meta?.object?.summary ??
        "";

    // Then fall back to summary if present
    const summary = a?.summary ?? "";

    return safeStr(metaContent).trim() || safeStr(summary).trim();
}


// ---------- Filter labels / grouping (restores your “specific” filters) ----------
const TYPE_LABELS = {
    ...usageContract.USAGE_LABELS,
    // Users / identity
    user_registered: "User registered",
    username_set: "Username set",
    space_header_updated: "Header updated",
    space_published: "Space published",
    space_deleted: "Space deleted",
    space_created: "Space created",

    // Components
    component_created: "Component created",
    component_updated: "Component updated",
    component_deleted: "Component deleted",

    // Posts
    post_created: "Post created",
    post_updated: "Post updated",
    post_deleted: "Post deleted",

    // Donations / mints
    donation_created: "Donation created",
    mint_created: "Mint created",
    donation_confirmed: "Donation confirmed",
    direct_payment_confirmed: "Direct payment confirmed",
    mint_confirmed: "Mint confirmed",

    // Legacy/generic action types you’ve seen in older docs
    Create: "Create (generic)",
    Update: "Update (generic)",
    Delete: "Delete (generic)",
};

const FILTER_GROUPS = [
    { label: "Homepage & login", types: Object.keys(usageContract.USAGE_LABELS) },
    {
        label: "User",
        types: ["user_registered", "username_set", "space_created", "space_header_updated", "space_published", "space_deleted"],
    },
    {
        label: "Components",
        types: ["component_created", "component_updated", "component_deleted"],
    },
    {
        label: "Posts",
        types: ["post_created", "post_updated", "post_deleted"],
    },
    {
        label: "Donations & Mints",
        types: ["donation_confirmed", "direct_payment_confirmed", "mint_confirmed", "donation_created", "mint_created"],
    },
    {
        label: "Generic",
        types: ["Create", "Update", "Delete"],
    },
];

function typeLabel(t) {
    const key = safeStr(t);
    if (!key) return "(no type)";
    return TYPE_LABELS[key] || key;
}


function fmtMaybeDate(v) {
    // supports Firestore Timestamp or JS Date
    try {
        if (!v) return "";
        if (v?.toDate) return v.toDate().toLocaleString();
        if (v instanceof Date) return v.toLocaleString();
        return "";
    } catch {
        return "";
    }
}

function spaceTitle(u) {
    return safeStr(u?.pageTitle || u?.muultiName || u?.id || "").trim() || "(untitled)";
}

function componentLabel(c) {
    const t = safeStr(c?.type);
    if (!t) return "(unknown)";

    if (t === "text") return safeStr(c?.heading || "Text");
    if (t === "link") return safeStr(c?.title || "Link");
    if (t === "video") return safeStr(c?.title || "Video");
    if (t === "audio") return safeStr(c?.title || "Audio");
    if (t === "image") return safeStr(c?.caption || c?.alt || "Image");
    if (t === "social") return "Social Links";
    return t;
}

function componentSummaryLine(c) {
    if (!c) return "";
    const t = safeStr(c.componentType || c.type).toLowerCase();
    if (t === "text") return safeStr(c.heading || c.subheading || c.excerpt || "Text");
    if (t === "link") return safeStr(c.title || c.url || "Link");
    if (t === "video") return safeStr(c.title || c.url || "Video");
    if (t === "image") return safeStr(c.caption || c.alt || c.url || "Image");
    if (t === "audio") return safeStr(c.title || c.url || "Audio");
    if (t === "donation") return safeStr(c.campaignName || "Donation");
    if (t === "mint") return safeStr(c.templateId || "Mint");
    if (t === "html") return "HTML component";
    if (t === "social") return "Social links";
    return safeStr(c.componentType || c.type || "Component");
}

function componentGateLabel(c) {
    const gt = safeStr(c?.gate?.type || "public");
    if (gt === "token") return `token (${(c?.gate?.tokenIds?.length ?? 0)})`;
    if (gt === "collection") return `collection (${(c?.gate?.collectionIds?.length ?? 0)})`;
    return gt;
}

function analyticsEventLabel(eventType) {
    const value = safeStr(eventType);
    if (value === "impression") return "Impression";
    if (value === "view") return "View";
    if (value === "interaction") return "Interaction";
    return value || "(unknown)";
}






export default function MuultiSpaceAdminDashboard() {
    const wallet = useWallet();
    const uid = wallet.publicKey?.toBase58?.() || '';
    return <AdminDashboardAccess key={uid + ':' + !!wallet.connected} wallet={wallet} />;
}

function AdminDashboardAccess({ wallet }) {
    const access = useAdminSession(wallet);
    const active = useRef(true);
    const [signing, setSigning] = useState(false);
    const [signError, setSignError] = useState('');
    useEffect(() => {
        active.current = true;
        return () => { active.current = false; };
    }, []);
    const signIn = async () => {
        if (signing) return;
        setSigning(true); setSignError('');
        try {
            await openPublishSessionForWallet(wallet, 'us-central1', { isCurrent: () => active.current && wallet.connected });
            if (active.current) access.retry();
        } catch (error) {
            if (active.current) setSignError(error.message || 'Wallet sign-in failed.');
        } finally { if (active.current) setSigning(false); }
    };
    if (access.authorized) return <AuthorizedAdminDashboard key={access.uid + ':' + access.version} wallet={wallet} authUid={access.uid} isAdmin />;
    return <div className="muulti-page"><div className="muulti-container muulti-admin-container">
        <h1>Admin sign-in</h1>
        {access.status === 'connect' && <p>Connect your admin wallet to continue.</p>}
        {access.status === 'checking' && <p role="status">Verifying admin access...</p>}
        {access.status === 'sign_in' && <>
            <p>Sign a message with your connected wallet to access the admin panel.</p>
            <button type="button" className="muulti-button" disabled={signing} onClick={signIn}>
                {signing ? 'Signing...' : 'Sign in with wallet'}
            </button>
        </>}
        {access.status === 'denied' && <p role="alert">This wallet is not authorized to access the admin panel.</p>}
        {access.error && <p role="alert">{access.error}</p>}
        {signError && <p role="alert">{signError}</p>}
        {['error', 'denied'].includes(access.status) && <button type="button" className="muulti-button" onClick={access.retry}>Retry access check</button>}
    </div></div>;
}

// This subtree mounts only after the connected wallet passes backend authorization.
// Disconnects, wallet changes and token changes unmount its data and settings.
function AuthorizedAdminDashboard({ wallet, authUid, isAdmin }) {
    const { connected, publicKey } = wallet;
    const walletStr = publicKey?.toBase58?.() || (publicKey ? String(publicKey) : "");

    // keep your existing state name
    const [typeFilter, setTypeFilter] = useState("all");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [lastDoc, setLastDoc] = useState(null);
    const [pagingLoading, setPagingLoading] = useState(false);
    const [activeAdminTab, setActiveAdminTab] = useState("analytics");
    const [analyticsItems, setAnalyticsItems] = useState([]);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsError, setAnalyticsError] = useState("");
    const [analyticsLastDoc, setAnalyticsLastDoc] = useState(null);
    const [analyticsPagingLoading, setAnalyticsPagingLoading] = useState(false);
    const [analyticsEventFilter, setAnalyticsEventFilter] = useState("all");
    const [analyticsPage, setAnalyticsPage] = useState(1);
    const [activityPage, setActivityPage] = useState(1);
    const [activityError, setActivityError] = useState("");
    const activityPageRef = useRef(activityPage);
    activityPageRef.current = activityPage;
    const walletRef = useRef(wallet);
    walletRef.current = wallet;
    // ---- Spaces explorer (users collection) ----
    const [spacesLoading, setSpacesLoading] = useState(false);
    const [spacesError, setSpacesError] = useState("");
    const [spaces, setSpaces] = useState([]); // each item: { id, ...data }
    const [spaceSearch, setSpaceSearch] = useState("");
    const [selectedSpaceId, setSelectedSpaceId] = useState("");
    const [selectedComponentId, setSelectedComponentId] = useState("");
    const [spacesPage, setSpacesPage] = useState(1);
    // Private audit reads use the existing backend wallet allowlist.
    useEffect(() => {
        if (!isAdmin || authUid !== walletStr) {
            setItems([]); setLastDoc(null); setLoading(false);
            return undefined;
        }
        if (activeAdminTab !== "notifications") return undefined;
        let cancelled = false;
        let refreshing = false;
        let initial = true;
        const refresh = async () => {
            if (refreshing || (!initial && (activityPageRef.current !== 1 || document.hidden))) return;
            refreshing = true;
            try {
                const snapshot = await loadAdminActivityEvents(walletRef.current);
                if (cancelled) return;
                if (initial) {
                    setItems(snapshot.items); setLastDoc(snapshot.cursor); setActivityPage(1);
                } else {
                    setItems(previous => {
                        const merged = new Map(previous.map(item => [item.id, item]));
                        snapshot.items.forEach(item => merged.set(item.id, item));
                        return Array.from(merged.values()).sort((a, b) => timestampMs(b.publishedAt) - timestampMs(a.publishedAt));
                    });
                }
                setActivityError(""); initial = false;
            } catch (error) {
                if (!cancelled) setActivityError(error.message || "Unable to load activity.");
            } finally {
                refreshing = false;
                if (!cancelled) setLoading(false);
            }
        };
        setLoading(true);
        refresh();
        const timer = setInterval(refresh, 30000);
        return () => { cancelled = true; clearInterval(timer); };
    }, [isAdmin, authUid, walletStr, activeAdminTab]);


    const filteredSpaces = useMemo(() => {
        const q = safeStr(spaceSearch).trim().toLowerCase();
        if (!q) return spaces;
        return spaces.filter((s) => {
            const name = safeStr(s.muultiName).toLowerCase();
            const title = safeStr(s.pageTitle).toLowerCase();
            const pk = safeStr(s.publicKey).toLowerCase();
            const id = safeStr(s.id).toLowerCase();
            return name.includes(q) || title.includes(q) || pk.includes(q) || id.includes(q);
        });
    }, [spaces, spaceSearch]);


    useEffect(() => {
        setSpacesPage(1);
    }, [spaceSearch]);

    useEffect(() => {
        // if refresh changes the list, clamp page
        setSpacesPage((p) => Math.max(1, p));
    }, [spaces]);

    const spacesTotalPages = useMemo(() => {
        const n = filteredSpaces.length;
        return Math.max(1, Math.ceil(n / SPACES_PAGE_SIZE));
    }, [filteredSpaces.length]);

    useEffect(() => {
        // clamp when filters change
        if (spacesPage > spacesTotalPages) setSpacesPage(spacesTotalPages);
    }, [spacesPage, spacesTotalPages]);

    const pagedSpaces = useMemo(() => {
        const start = (spacesPage - 1) * SPACES_PAGE_SIZE;
        return filteredSpaces.slice(start, start + SPACES_PAGE_SIZE);
    }, [filteredSpaces, spacesPage]);


    const loadSpaces = useCallback(async () => {
        if (!isAdmin) return;

        setSpacesLoading(true);
        setSpacesError("");
        try {
            // Admin-only view: pull user docs (spaces).
            // Using getDocs (not realtime) to avoid heavy onSnapshot on large collections.
            const snap = await getDocs(collection(db, "users"));
            const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

            // Optional: sort with best-effort keys (no Firestore orderBy dependency)
            list.sort((a, b) => {
                const an = safeStr(a.muultiName).toLowerCase();
                const bn = safeStr(b.muultiName).toLowerCase();
                if (an && bn) return an.localeCompare(bn);
                if (an) return -1;
                if (bn) return 1;
                // fall back to updatedAt/createdAt if they exist
                const ad = a.updatedAt?.toDate?.() || a.createdAt?.toDate?.() || null;
                const bd = b.updatedAt?.toDate?.() || b.createdAt?.toDate?.() || null;
                if (ad && bd) return bd - ad;
                if (ad) return -1;
                if (bd) return 1;
                return 0;
            });

            setSpaces(list);

            // keep selection stable if possible
            if (selectedSpaceId && !list.some((s) => s.id === selectedSpaceId)) {
                setSelectedSpaceId("");
                setSelectedComponentId("");
            }
        } catch (e) {
            console.error("Admin loadSpaces error:", e);
            setSpaces([]);
            setSpacesError("Failed to load spaces.");
        } finally {
            setSpacesLoading(false);
        }
    }, [isAdmin, selectedSpaceId]);

    useEffect(() => {
        if (!isAdmin) {
            setSpaces([]);
            setSelectedSpaceId("");
            setSelectedComponentId("");
            setAnalyticsItems([]);
            setAnalyticsLastDoc(null);
            return;
        }
        loadSpaces();
    }, [isAdmin, loadSpaces]);

    const loadAnalyticsEvents = useCallback(async () => {
        if (!isAdmin) return;

        setAnalyticsLoading(true);
        setAnalyticsError("");
        try {
            const snap = await getDocs(
                query(
                    collection(db, "componentAnalyticsEvents"),
                    orderBy("createdAt", "desc"),
                    limit(ANALYTICS_PAGE_SIZE)
                )
            );
            setAnalyticsItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setAnalyticsLastDoc(snap.docs.length ? snap.docs[snap.docs.length - 1] : null);
        } catch (e) {
            console.error("Admin analytics load error:", e);
            setAnalyticsItems([]);
            setAnalyticsLastDoc(null);
            setAnalyticsError("Failed to load component analytics events.");
        } finally {
            setAnalyticsLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        if (!isAdmin) return;
        loadAnalyticsEvents();
    }, [isAdmin, loadAnalyticsEvents]);

    const loadMoreAnalyticsEvents = useCallback(async () => {
        if (!isAdmin || !analyticsLastDoc || analyticsPagingLoading) return 0;

        setAnalyticsPagingLoading(true);
        setAnalyticsError("");
        try {
            const snap = await getDocs(
                query(
                    collection(db, "componentAnalyticsEvents"),
                    orderBy("createdAt", "desc"),
                    startAfter(analyticsLastDoc),
                    limit(ANALYTICS_PAGE_SIZE)
                )
            );
            const more = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setAnalyticsItems((prev) => [...prev, ...more]);
            setAnalyticsLastDoc(snap.docs.length ? snap.docs[snap.docs.length - 1] : null);
            return more.length;
        } catch (e) {
            console.error("Admin analytics loadMore error:", e);
            setAnalyticsError("Failed to load more component analytics events.");
            return 0;
        } finally {
            setAnalyticsPagingLoading(false);
        }
    }, [analyticsLastDoc, analyticsPagingLoading, isAdmin]);


    const loadMore = useCallback(async () => {
        if (!isAdmin) return 0;
        if (!lastDoc) return 0;
        if (pagingLoading) return 0;

        setPagingLoading(true);
        try {
            const snapshot = await loadAdminActivityEvents(walletRef.current, lastDoc);
            const more = snapshot.items;
            setItems(previous => Array.from(new Map([...previous, ...more].map(item => [item.id, item])).values()));
            setLastDoc(snapshot.cursor);
            setActivityError("");
            return more.length;
        } catch (e) {
            console.error("Admin activities loadMore error:", e);
            setActivityError(e.message || "Unable to load more activity.");
            return 0;
        } finally {
            setPagingLoading(false);
        }
    }, [isAdmin, lastDoc, pagingLoading]);

    const filtered = useMemo(() => {
        if (typeFilter === "all") return items;
        return items.filter(
            (it) => safeStr(it.type).toLowerCase() === safeStr(typeFilter).toLowerCase()
        );
    }, [items, typeFilter]);

    const filteredAnalyticsItems = useMemo(() => {
        if (analyticsEventFilter === "all") return analyticsItems;
        return analyticsItems.filter((event) => safeStr(event.eventType) === analyticsEventFilter);
    }, [analyticsEventFilter, analyticsItems]);

    useEffect(() => {
        setActivityPage(1);
    }, [typeFilter]);

    useEffect(() => {
        setAnalyticsPage(1);
    }, [analyticsEventFilter]);

    const activityTotalPages = useMemo(() => {
        const n = filtered.length;
        return Math.max(1, Math.ceil(n / ADMIN_DISPLAY_PAGE_SIZE));
    }, [filtered.length]);

    useEffect(() => {
        if (activityPage > activityTotalPages) setActivityPage(activityTotalPages);
    }, [activityPage, activityTotalPages]);

    const pagedActivityItems = useMemo(() => {
        const start = (activityPage - 1) * ADMIN_DISPLAY_PAGE_SIZE;
        return filtered.slice(start, start + ADMIN_DISPLAY_PAGE_SIZE);
    }, [activityPage, filtered]);

    const analyticsTotalPages = useMemo(() => {
        const n = filteredAnalyticsItems.length;
        return Math.max(1, Math.ceil(n / ADMIN_DISPLAY_PAGE_SIZE));
    }, [filteredAnalyticsItems.length]);

    useEffect(() => {
        if (analyticsPage > analyticsTotalPages) setAnalyticsPage(analyticsTotalPages);
    }, [analyticsPage, analyticsTotalPages]);

    const pagedAnalyticsItems = useMemo(() => {
        const start = (analyticsPage - 1) * ADMIN_DISPLAY_PAGE_SIZE;
        return filteredAnalyticsItems.slice(start, start + ADMIN_DISPLAY_PAGE_SIZE);
    }, [analyticsPage, filteredAnalyticsItems]);

    const handleActivityPrevPage = useCallback(() => {
        setActivityPage((p) => Math.max(1, p - 1));
    }, []);

    const handleActivityNextPage = useCallback(async () => {
        if (activityPage < activityTotalPages) {
            setActivityPage((p) => Math.min(activityTotalPages, p + 1));
            return;
        }

        const loaded = await loadMore();
        if (loaded > 0) {
            setActivityPage((p) => p + 1);
        }
    }, [activityPage, activityTotalPages, loadMore]);

    const handleAnalyticsPrevPage = useCallback(() => {
        setAnalyticsPage((p) => Math.max(1, p - 1));
    }, []);

    const handleAnalyticsNextPage = useCallback(async () => {
        if (analyticsPage < analyticsTotalPages) {
            setAnalyticsPage((p) => Math.min(analyticsTotalPages, p + 1));
            return;
        }

        const loaded = await loadMoreAnalyticsEvents();
        if (loaded > 0) {
            setAnalyticsPage((p) => p + 1);
        }
    }, [analyticsPage, analyticsTotalPages, loadMoreAnalyticsEvents]);

    const analyticsLoadedCounts = useMemo(() => {
        return analyticsItems.reduce((acc, event) => {
            const key = safeStr(event.eventType) || "unknown";
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
    }, [analyticsItems]);



    const renderSelectedSpaceRowDetails = () => {
        if (!selectedSpace) return null;

        return (
            <div className="admin-space-detail" style={{ padding: "12px 10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                        <div style={{ fontWeight: 900, fontSize: 14 }}>
                            Components for: {spaceTitle(selectedSpace)}
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.75 }}>
                            uid: <code>{selectedSpace.id}</code>
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <select
                            className="admin-select"
                            value={selectedComponentId}
                            onChange={(e) => setSelectedComponentId(e.target.value)}
                            style={{
                                height: 34,
                                padding: "0 12px",
                                borderRadius: 999,
                                minWidth: 320,
                            }}
                        >
                            <option value="">Select a component…</option>
                            {selectedSpaceComponents.map((c, idx) => (
                                <option key={`${safeStr(c.id) || idx}`} value={safeStr(c.id)}>
                                    {idx + 1}. {safeStr(c.type)} · {clampText(componentLabel(c), 64)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {selectedComponent && (
                    <details style={{ marginTop: 10 }}>
                        <summary style={{ cursor: "pointer", opacity: 0.9 }}>
                            Selected component JSON (id: {safeStr(selectedComponent.id) || "(none)"})
                        </summary>
                        <pre
                            className="admin-json"
                            style={{
                                marginTop: 8,
                                padding: 12,
                                borderRadius: 10,
                                overflowX: "auto",
                                fontSize: 11,
                            }}
                        >
{prettyJson(selectedComponent)}
                    </pre>
                    </details>
                )}

                <div style={{ marginTop: 10, overflowX: "auto" }}>
                    <table className="admin-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                        <tr style={{ textAlign: "left" }}>
                            <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>#</th>
                            <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>Type</th>
                            <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>Label</th>
                            <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>Gate</th>
                            <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>Component ID</th>
                        </tr>
                        </thead>
                        <tbody>
                        {selectedSpaceComponents.map((c, idx) => {
                            const cid = safeStr(c?.id);
                            const rowSelected = selectedComponentId && safeStr(selectedComponentId) === cid;

                            return (
                                <tr
                                    key={`${cid || idx}`}
                                    style={{
                                        background: rowSelected ? "rgba(123,194,182,0.15)" : "transparent",
                                        cursor: cid ? "pointer" : "default",
                                    }}
                                    onClick={() => cid && setSelectedComponentId(cid)}
                                >
                                    <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{idx + 1}</td>
                                    <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{safeStr(c?.type) || "(none)"}</td>
                                    <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{clampText(componentLabel(c), 120) || "(no label)"}</td>
                                    <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{componentGateLabel(c)}</td>
                                    <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontFamily: "monospace", fontSize: 12 }}>
                                        {cid || "(none)"}
                                    </td>
                                </tr>
                            );
                        })}

                        {selectedSpaceComponents.length === 0 && (
                            <tr>
                                <td colSpan={5} style={{ padding: 14, opacity: 0.7 }}>
                                    This space has no components.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };


    // ✅ Build dropdown options:
    // 1) Always include your known “specific” types (even if none currently exist)
    // 2) Include any additional types found in data
    const typeOptions = useMemo(() => {
        const fromData = new Set();
        for (const it of items) {
            const t = safeStr(it.type);
            if (t) fromData.add(t);
        }

        const known = new Set();
        for (const g of FILTER_GROUPS) {
            for (const t of g.types) known.add(t);
        }

        const extras = Array.from(fromData)
            .filter((t) => !known.has(t))
            .sort((a, b) => a.localeCompare(b));

        return {
            groups: FILTER_GROUPS.map((g) => ({
                label: g.label,
                types: g.types,
            })),
            extras,
        };
    }, [items]);




    const selectedSpace = useMemo(() => {
        return spaces.find((s) => s.id === selectedSpaceId) || null;
    }, [spaces, selectedSpaceId]);

    const selectedSpaceComponents = useMemo(() => {
        const arr = selectedSpace?.components;
        return Array.isArray(arr) ? arr : [];
    }, [selectedSpace]);

    useEffect(() => {
        // reset component selection when switching spaces
        setSelectedComponentId("");
    }, [selectedSpaceId]);

    const selectedComponent = useMemo(() => {
        if (!selectedComponentId) return null;
        return selectedSpaceComponents.find((c) => safeStr(c.id) === safeStr(selectedComponentId)) || null;
    }, [selectedSpaceComponents, selectedComponentId]);

    const renderActivityFilter = () => (
        <div
            style={{
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
                alignItems: "center",
                margin: "0 0 16px",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.8 }}>Filter:</div>
                <select
                    className="admin-select"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    style={{
                        height: 34,
                        padding: "0 10px",
                        borderRadius: 999,
                    }}
                >
                    <option value="all">All activity</option>

                    {typeOptions.groups.map((g) => (
                        <optgroup key={g.label} label={g.label}>
                            {g.types.map((t) => (
                                <option key={t} value={t}>
                                    {typeLabel(t)}
                                </option>
                            ))}
                        </optgroup>
                    ))}

                    {typeOptions.extras.length > 0 && (
                        <optgroup label="Other (from data)">
                            {typeOptions.extras.map((t) => (
                                <option key={t} value={t}>
                                    {typeLabel(t)}
                                </option>
                            ))}
                        </optgroup>
                    )}
                </select>
            </div>

            <div style={{ fontSize: 12, opacity: 0.7 }}>
                Newest first. Refreshes every 30 seconds on the first page. Filtering is client-side.
            </div>
        </div>
    );

    const renderAdminTabs = () => {
        const tabs = [
            { id: "analytics", label: "Analytics Events" },
            { id: "components", label: "Component Data" },
            { id: "inbox", label: "Notification Inbox" },
            { id: "notifications", label: "Activity Audit" },
        ];

        return (
            <div className="admin-dashboard-tabs" role="tablist" aria-label="Admin dashboard sections">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeAdminTab === tab.id}
                        className={`admin-dashboard-tab${activeAdminTab === tab.id ? " is-active" : ""}`}
                        onClick={() => setActiveAdminTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    const renderAnalyticsEvents = () => (
        <div className="admin-panel admin-panel--analytics" style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                    <div style={{ fontWeight: 900, fontSize: 16 }}>Component Analytics Events</div>
                    <div style={{ fontSize: 12, opacity: 0.75 }}>
                        Newest events from <code>componentAnalyticsEvents</code>. Each row is one impression, view, or interaction.
                    </div>
                </div>

                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <select
                        className="admin-select"
                        value={analyticsEventFilter}
                        onChange={(e) => setAnalyticsEventFilter(e.target.value)}
                        style={{ height: 34, padding: "0 12px", borderRadius: 999 }}
                    >
                        <option value="all">All events</option>
                        <option value="impression">Impressions</option>
                        <option value="view">Views</option>
                        <option value="interaction">Interactions</option>
                    </select>
                    <button
                        type="button"
                        className="muulti-button"
                        onClick={loadAnalyticsEvents}
                        disabled={analyticsLoading}
                        style={{ width: "auto", padding: "6px 14px" }}
                    >
                        {analyticsLoading ? "Refreshing..." : "Refresh"}
                    </button>
                </div>
            </div>

            <div className="admin-analytics-counts">
                <div><b>{analyticsItems.length}</b><span>loaded</span></div>
                <div><b>{analyticsLoadedCounts.impression || 0}</b><span>impressions</span></div>
                <div><b>{analyticsLoadedCounts.view || 0}</b><span>views</span></div>
                <div><b>{analyticsLoadedCounts.interaction || 0}</b><span>interactions</span></div>
            </div>

            {analyticsError && (
                <div style={{ marginTop: 10, color: "#c00", fontSize: 13 }}>{analyticsError}</div>
            )}

            {analyticsLoading ? (
                <div style={{ padding: 16, opacity: 0.75 }}>Loading analytics...</div>
            ) : (
                <>
                    <div style={{ marginTop: 12, fontSize: 12, opacity: 0.8 }}>
                        Loaded: <b>{analyticsItems.length}</b> events · Matching: <b>{filteredAnalyticsItems.length}</b> · Page{" "}
                        <b>{analyticsPage}</b>/<b>{analyticsTotalPages}{analyticsLastDoc ? "+" : ""}</b>
                    </div>

                    <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <div style={{ fontSize: 12, opacity: 0.75 }}>
                            Showing {filteredAnalyticsItems.length ? (analyticsPage - 1) * ADMIN_DISPLAY_PAGE_SIZE + 1 : 0}{Math.min(analyticsPage * ADMIN_DISPLAY_PAGE_SIZE, filteredAnalyticsItems.length)} of {filteredAnalyticsItems.length}
                        </div>

                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <button
                                type="button"
                                className="muulti-button"
                                style={{ width: "auto", padding: "6px 12px", opacity: analyticsPage <= 1 || analyticsPagingLoading ? 0.5 : 1 }}
                                disabled={analyticsPage <= 1 || analyticsPagingLoading}
                                onClick={handleAnalyticsPrevPage}
                            >
                                Prev
                            </button>

                            <button
                                type="button"
                                className="muulti-button"
                                style={{ width: "auto", padding: "6px 12px", opacity: (analyticsPage >= analyticsTotalPages && !analyticsLastDoc) || analyticsPagingLoading ? 0.5 : 1 }}
                                disabled={(analyticsPage >= analyticsTotalPages && !analyticsLastDoc) || analyticsPagingLoading}
                                onClick={handleAnalyticsNextPage}
                            >
                                {analyticsPagingLoading ? "Loading..." : "Next"}
                            </button>
                        </div>
                    </div>

                    <div style={{ marginTop: 10, overflowX: "auto" }}>
                        <table className="admin-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                            <tr style={{ textAlign: "left" }}>
                                <th style={{ padding: "10px 8px" }}>Time</th>
                                <th style={{ padding: "10px 8px" }}>Event</th>
                                <th style={{ padding: "10px 8px" }}>Space</th>
                                <th style={{ padding: "10px 8px" }}>Component</th>
                                <th style={{ padding: "10px 8px" }}>Surface</th>
                                <th style={{ padding: "10px 8px" }}>Path</th>
                                <th style={{ padding: "10px 8px" }}>Session</th>
                            </tr>
                            </thead>
                            <tbody>
                            {pagedAnalyticsItems.map((event) => {
                                const ownerUsername = safeStr(event.ownerUsername);
                                const ownerPublicKey = safeStr(event.ownerPublicKey);
                                const componentId = safeStr(event.componentId);
                                const sessionId = safeStr(event.sessionId);

                                return (
                                    <tr key={event.id}>
                                        <td style={{ padding: "10px 8px", fontSize: 12, whiteSpace: "nowrap" }}>
                                            {fmtMaybeDate(event.createdAt) || "(pending)"}
                                        </td>
                                        <td style={{ padding: "10px 8px", fontWeight: 800 }}>
                                            {analyticsEventLabel(event.eventType)}
                                        </td>
                                        <td style={{ padding: "10px 8px" }}>
                                            {ownerUsername ? (
                                                <Link to={`/${ownerUsername}`}>{ownerUsername}</Link>
                                            ) : (
                                                "(unknown)"
                                            )}
                                            {ownerPublicKey && (
                                                <div style={{ fontFamily: "monospace", fontSize: 11, opacity: 0.75 }}>
                                                    {ownerPublicKey.slice(0, 6)}...{ownerPublicKey.slice(-6)}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: "10px 8px" }}>
                                            <div>{safeStr(event.componentType) || "(none)"}</div>
                                            <div style={{ fontFamily: "monospace", fontSize: 11, opacity: 0.75 }}>
                                                {componentId || "(no id)"}
                                            </div>
                                        </td>
                                        <td style={{ padding: "10px 8px" }}>{safeStr(event.surface) || "(none)"}</td>
                                        <td style={{ padding: "10px 8px", maxWidth: 280 }}>
                                            <span style={{ overflowWrap: "anywhere" }}>{safeStr(event.path) || "(none)"}</span>
                                        </td>
                                        <td style={{ padding: "10px 8px", fontFamily: "monospace", fontSize: 11 }}>
                                            {sessionId ? `${sessionId.slice(0, 8)}...` : "(none)"}
                                        </td>
                                    </tr>
                                );
                            })}

                            {pagedAnalyticsItems.length === 0 && (
                                <tr>
                                    <td colSpan={7} style={{ padding: 14, opacity: 0.7 }}>
                                        No analytics events match this filter yet.
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );


    return (
        <div className="muulti-page">
            <div className="muulti-container muulti-admin-container">
                <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                    <h1 style={{ margin: "12px 0" }}>Admin Dashboard</h1>
                    <div style={{ opacity: 0.75, fontSize: 12 }}>
                        Signed in as {authUid || "(none)"} · wallet{" "}
                        {walletStr ? `${walletStr.slice(0, 4)}…${walletStr.slice(-4)}` : "(none)"}
                    </div>
                </div>



                {isAdmin && <PublishingFeePolicy />}
                {isAdmin && connected && authUid === walletStr && <PublishingCredentials key={walletStr} />}
                {isAdmin && renderAdminTabs()}

                {isAdmin && activeAdminTab === "analytics" && <>
                    <UsageAnalytics wallet={wallet} />
                    {renderAnalyticsEvents()}
                </>}

                {isAdmin && activeAdminTab === "inbox" && (
                    <div className="admin-panel" style={{ padding: 16 }}>
                        <NotificationInbox wallet={wallet} admin />
                    </div>
                )}

                {isAdmin && ["components", "notifications"].includes(activeAdminTab) && (
                <div className="admin-dashboard-layout">
                    {activeAdminTab === "notifications" && (
                    <aside className="admin-dashboard-notifications">
                {renderActivityFilter()}
                {activityError && <p role="alert">{activityError}</p>}
                {!loading && filtered.length === 0 && lastDoc && (
                    <button type="button" className="muulti-button" onClick={loadMore} disabled={pagingLoading}>
                        {pagingLoading ? "Loading..." : "Search older activity"}
                    </button>
                )}
                {isAdmin && loading && <div style={{ padding: 16, opacity: 0.75 }}>Loading activity…</div>}

                {isAdmin && !loading && filtered.length === 0 && (
                    <div style={{ padding: 24, textAlign: "center", opacity: 0.7 }}>No activity yet.</div>
                )}




                {isAdmin && filtered.length > 0 && (
                    <div style={{ display: "grid", gap: 14 }}>
                        <div style={{ fontSize: 12, opacity: 0.8 }}>
                            Loaded: <b>{items.length}</b> notifications · Matching: <b>{filtered.length}</b> · Page{" "}
                            <b>{activityPage}</b>/<b>{activityTotalPages}{lastDoc ? "+" : ""}</b>
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                            <div style={{ fontSize: 12, opacity: 0.75 }}>
                                Showing {filtered.length ? (activityPage - 1) * ADMIN_DISPLAY_PAGE_SIZE + 1 : 0}{Math.min(activityPage * ADMIN_DISPLAY_PAGE_SIZE, filtered.length)} of {filtered.length}
                            </div>

                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <button
                                    type="button"
                                    className="muulti-button"
                                    style={{ width: "auto", padding: "6px 12px", opacity: activityPage <= 1 || pagingLoading ? 0.5 : 1 }}
                                    disabled={activityPage <= 1 || pagingLoading}
                                    onClick={handleActivityPrevPage}
                                >
                                    Prev
                                </button>

                                <button
                                    type="button"
                                    className="muulti-button"
                                    style={{ width: "auto", padding: "6px 12px", opacity: (activityPage >= activityTotalPages && !lastDoc) || pagingLoading ? 0.5 : 1 }}
                                    disabled={(activityPage >= activityTotalPages && !lastDoc) || pagingLoading}
                                    onClick={handleActivityNextPage}
                                >
                                    {pagingLoading ? "Loading" : "Next"}
                                </button>
                            </div>
                        </div>

                        {pagedActivityItems.map((a) => {
                            // ✅ prefer ORDER_FIELD, but still display whatever exists
                            const when = fmtTime(a?.[ORDER_FIELD] || a.createdAt || a.updatedAt);

                            const actorUid = safeStr(a.actorUid);
                            const actorName = safeStr(a.actorName);
                            const subjectUid = safeStr(a.subjectUid);
                            const subjectId = safeStr(a.subjectId);
                            const objectId = safeStr(a.objectId);

                            const activityText = pickActivityText(a);
                            const activityTextShort = clampText(activityText, 260);

                            const type = safeStr(a.type) || "(no type)";
                            const prettyType = typeLabel(type);

                            const to = Array.isArray(a.to) ? a.to.join(", ") : safeStr(a.to);
                            const target = safeStr(a.target);

                            const meta = a.meta;
                            const compSummary = meta?.componentSummary || null;
                            const compRaw = meta?.component || null;
                            const spaceMeta = meta?.space || {};
                            const spaceName = safeStr(spaceMeta.muultiName);
                            const spaceLink = spaceName
                                ? `/${spaceName}`
                                : "";

                              return (
                                  <div
                                      key={a.id}
                                      className="admin-activity-card"
                                  >
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                        <div style={{ fontWeight: 800 }}>{prettyType}</div>
                                        <div style={{ fontSize: 12, opacity: 0.7 }}>{when}</div>
                                    </div>

                                    <div className={`admin-activity-text ${activityText ? "has-content" : "is-empty"}`}>
                                        {activityText ? activityTextShort : "(no summary)"}

                                        {activityText && activityText.length > 260 && (
                                            <details style={{ marginTop: 8 }}>
                                                <summary style={{ cursor: "pointer", opacity: 0.85 }}>Show full text</summary>
                                                <div className="admin-activity-text-full">
                                                    {activityText}
                                                </div>
                                            </details>
                                        )}
                                    </div>

                                    <div className="admin-activity-meta">
                                        {a.meta?.evidence && <div><b>evidence:</b> {a.origin === 'legacy' ? 'Unverified history' : a.meta.evidence === 'server_verified' ? 'Server verified' : 'Browser reported'}</div>}
                                        {a.meta?.flow && <div><b>flow:</b> {a.meta.flow}</div>}
                                        {a.meta?.failureCode && <div><b>failure:</b> {a.meta.failureCode}</div>}
                                        {a.transactionStatus && a.transactionStatus !== "not_applicable" && (
                                            <div><b>transaction:</b> {a.transactionStatus} · {a.origin === "legacy" ? "unverified history" : "backend verified"}</div>
                                        )}
                                        {(actorName || actorUid) && (
                                            <div>
                                                <b>actor:</b>{" "}
                                                {actorName ? `${actorName} (${actorUid || "no uid"})` : actorUid}
                                            </div>
                                        )}

                                        {spaceLink && (
                                            <div>
                                                <b>space:</b>{" "}
                                                <Link to={spaceLink}>{spaceName}</Link>
                                            </div>
                                        )}

                                        {(subjectUid || subjectId) && (
                                            <div>
                                                <b>subject:</b> {subjectUid ? subjectUid : "(none)"}{" "}
                                                {subjectId ? `· ${subjectId}` : ""}
                                            </div>
                                        )}

                                        {(compSummary || compRaw) && (
                                            <div>
                                                <b>component:</b>{" "}
                                                {componentSummaryLine(compSummary || compRaw)}
                                                {(compRaw?.url || compSummary?.url) ? (
                                                    <span>
                                                        {" "}·{" "}
                                                        <a href={compRaw?.url || compSummary?.url} target="_blank" rel="noreferrer">
                                                            link
                                                        </a>
                                                    </span>
                                                ) : null}
                                            </div>
                                        )}

                                        {objectId && (
                                            <div>
                                                <b>objectId:</b> {objectId}
                                            </div>
                                        )}

                                        {(to || target) && (
                                            <div>
                                                <b>route:</b> {to ? `to=[${to}]` : ""} {target ? ` target=${target}` : ""}
                                            </div>
                                        )}

                                        {meta != null && (
                                            <details style={{ marginTop: 6 }}>
                                                <summary style={{ cursor: "pointer", opacity: 0.85 }}>meta</summary>
                                                <pre
                                                    className="admin-json"
                                                    style={{
                                                        marginTop: 8,
                                                        padding: 12,
                                                        borderRadius: 10,
                                                        overflowX: "auto",
                                                        fontSize: 11,
                                                    }}
                                                >
                                {prettyJson(meta)}
                                                    </pre>
                                            </details>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                    </div>
                )}
                    </aside>
                    )}




                    {activeAdminTab === "components" && (
                    <main className="admin-dashboard-main">
                {isAdmin && (
                    <div
                        className="admin-panel admin-panel--spaces"
                        style={{
                            margin: "14px 0 18px",
                            padding: 16,
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                            <div>
                                <div style={{ fontWeight: 900, fontSize: 16 }}>Spaces Explorer</div>
                                <div style={{ fontSize: 12, opacity: 0.75 }}>
                                    This queries the <code>users</code> collection (each doc = a space).
                                </div>
                            </div>

                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                <input className="admin-input"
                                    value={spaceSearch}
                                    onChange={(e) => setSpaceSearch(e.target.value)}
                                    placeholder="Search by muultiName, title, uid, publicKey…"
                                    style={{
                                        height: 34,
                                        minWidth: 320,
                                        padding: "0 12px",
                                        borderRadius: 999,
                                    }}
                                />
                                <button
                                    type="button"
                                    className="muulti-button"
                                    onClick={loadSpaces}
                                    disabled={spacesLoading}
                                    style={{ width: "auto", padding: "6px 14px" }}
                                >
                                    {spacesLoading ? "Refreshing…" : "Refresh"}
                                </button>
                            </div>
                        </div>

                        {spacesError && (
                            <div style={{ marginTop: 10, color: "#c00", fontSize: 13 }}>{spacesError}</div>
                        )}

                        <div style={{ marginTop: 12, fontSize: 12, opacity: 0.8 }}>
                            Loaded: <b>{spaces.length}</b> spaces · Matching: <b>{filteredSpaces.length}</b> · Page{" "}
                            <b>{spacesPage}</b>/<b>{spacesTotalPages}</b>
                        </div>


                        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                            <div style={{ fontSize: 12, opacity: 0.75 }}>
                                Showing {filteredSpaces.length ? (spacesPage - 1) * SPACES_PAGE_SIZE + 1 : 0}–{Math.min(spacesPage * SPACES_PAGE_SIZE, filteredSpaces.length)} of {filteredSpaces.length}
                            </div>

                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <button
                                    type="button"
                                    className="muulti-button"
                                    style={{ width: "auto", padding: "6px 12px", opacity: spacesPage <= 1 ? 0.5 : 1 }}
                                    disabled={spacesPage <= 1}
                                    onClick={() => setSpacesPage((p) => Math.max(1, p - 1))}
                                >
                                    Prev
                                </button>

                                <button
                                    type="button"
                                    className="muulti-button"
                                    style={{ width: "auto", padding: "6px 12px", opacity: spacesPage >= spacesTotalPages ? 0.5 : 1 }}
                                    disabled={spacesPage >= spacesTotalPages}
                                    onClick={() => setSpacesPage((p) => Math.min(spacesTotalPages, p + 1))}
                                >
                                    Next
                                </button>
                            </div>
                        </div>



                        {/* Spaces table */}
                        <div style={{ marginTop: 10, overflowX: "auto" }}>
                            <table className="admin-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                <thead>
                                <tr style={{ textAlign: "left" }}>
                                    <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                        Space
                                    </th>
                                    <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                        UID
                                    </th>
                                    <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                        Template
                                    </th>
                                    <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                        Components
                                    </th>
                                    <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                        Updated
                                    </th>
                                    <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                {pagedSpaces.map((s) => {
                                    const comps = Array.isArray(s.components) ? s.components : [];
                                    const when = fmtMaybeDate(s.updatedAt) || fmtMaybeDate(s.createdAt);
                                    const isSelected = s.id === selectedSpaceId;

                                    return (
                                        <React.Fragment key={s.id}>
                                            <tr style={{ background: isSelected ? "rgba(123,194,182,0.15)" : "transparent" }}>
                                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                                                    <div style={{ fontWeight: 800 }}>{spaceTitle(s)}</div>
                                                    <div style={{ fontSize: 12, opacity: 0.75 }}>
                                                        muultiName: <b>{safeStr(s.muultiName) || "(none)"}</b>
                                                    </div>
                                                </td>

                                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontFamily: "monospace", fontSize: 12 }}>
                                                    {safeStr(s.id).slice(0, 6)}…{safeStr(s.id).slice(-6)}
                                                    {s.publicKey ? (
                                                        <div style={{ marginTop: 4, opacity: 0.75 }}>
                                                            pk: {safeStr(s.publicKey).slice(0, 6)}…{safeStr(s.publicKey).slice(-6)}
                                                        </div>
                                                    ) : null}
                                                </td>

                                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                                                    {safeStr(s.template) || "(none)"}
                                                </td>

                                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                                                    <b>{comps.length}</b>
                                                    <div style={{ fontSize: 12, opacity: 0.75 }}>
                                                        {(() => {
                                                            const counts = {};
                                                            comps.forEach((c) => {
                                                                const t = safeStr(c?.type || "unknown");
                                                                counts[t] = (counts[t] || 0) + 1;
                                                            });
                                                            const parts = Object.entries(counts).map(([k, v]) => `${k}:${v}`);
                                                            return parts.slice(0, 4).join(" · ") + (parts.length > 4 ? " · …" : "");
                                                        })()}
                                                    </div>
                                                </td>

                                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 12, opacity: 0.85 }}>
                                                    {when || "(n/a)"}
                                                </td>

                                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        style={{ width: "auto", padding: "6px 12px" }}
                                                        onClick={() => {
                                                            setSelectedSpaceId(isSelected ? "" : s.id);
                                                            // ensure the dropdown "row detail" follows the selection
                                                            // (component selection reset already handled by effect)
                                                        }}
                                                    >
                                                        {isSelected ? "Hide components" : "View components"}
                                                    </button>
                                                </td>
                                            </tr>

                                            {isSelected && (
                                                <tr>
                                                    <td
                                                        colSpan={6}
                                                        className="admin-table-detail"
                                                        style={{
                                                            padding: 0,
                                                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                                                        }}
                                                    >
                                                        {renderSelectedSpaceRowDetails()}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}


                                {filteredSpaces.length === 0 && (
                                    <tr>
                                        <td colSpan={6} style={{ padding: 14, opacity: 0.7 }}>
                                            No spaces match your search.
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>

                        {/* Selected space details */}
                        {selectedSpace && (
                            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                                    <div>
                                        <div style={{ fontWeight: 900, fontSize: 14 }}>
                                            Components for: {spaceTitle(selectedSpace)}
                                        </div>
                                        <div style={{ fontSize: 12, opacity: 0.75 }}>
                                            uid: <code>{selectedSpace.id}</code>
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                        <select className="admin-select"
                                            value={selectedComponentId}
                                            onChange={(e) => setSelectedComponentId(e.target.value)}
                                            style={{
                                                height: 34,
                                                padding: "0 12px",
                                                borderRadius: 999,
                                                minWidth: 320,
                                            }}
                                        >
                                            <option value="">Select a component…</option>
                                            {selectedSpaceComponents.map((c, idx) => (
                                                <option key={`${safeStr(c.id) || idx}`} value={safeStr(c.id)}>
                                                    {idx + 1}. {safeStr(c.type)} · {clampText(componentLabel(c), 64)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {selectedComponent && (
                                    <details style={{ marginTop: 10 }}>
                                        <summary style={{ cursor: "pointer", opacity: 0.9 }}>
                                            Selected component JSON (id: {safeStr(selectedComponent.id) || "(none)"})
                                        </summary>
                                        <pre
                                            className="admin-json"
                                            style={{
                                                marginTop: 8,
                                                padding: 12,
                                                borderRadius: 10,
                                                overflowX: "auto",
                                                fontSize: 11,
                                            }}
                                        >
                                        {prettyJson(selectedComponent)}
                                        </pre>
                                    </details>
                                )}

                                <div style={{ marginTop: 10, overflowX: "auto" }}>
                                    <table className="admin-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                        <thead>
                                        <tr style={{ textAlign: "left" }}>
                                            <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                #
                                            </th>
                                            <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                Type
                                            </th>
                                            <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                Label
                                            </th>
                                            <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                Gate
                                            </th>
                                            <th style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                Component ID
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {selectedSpaceComponents.map((c, idx) => {
                                            const cid = safeStr(c?.id);
                                            const rowSelected = selectedComponentId && safeStr(selectedComponentId) === cid;

                                            return (
                                                <tr
                                                    key={`${cid || idx}`}
                                                    style={{ background: rowSelected ? "rgba(123,194,182,0.15)" : "transparent", cursor: cid ? "pointer" : "default" }}
                                                    onClick={() => cid && setSelectedComponentId(cid)}
                                                >
                                                    <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                                                        {idx + 1}
                                                    </td>
                                                    <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                                                        {safeStr(c?.type) || "(none)"}
                                                    </td>
                                                    <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                                                        {clampText(componentLabel(c), 120) || "(no label)"}
                                                    </td>
                                                    <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                                                        {componentGateLabel(c)}
                                                    </td>
                                                    <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontFamily: "monospace", fontSize: 12 }}>
                                                        {cid || "(none)"}
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        {selectedSpaceComponents.length === 0 && (
                                            <tr>
                                                <td colSpan={5} style={{ padding: 14, opacity: 0.7 }}>
                                                    This space has no components.
                                                </td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                </main>
                    )}
                </div>
                )}


            </div>
        </div>
    );
}
